import type { HawbEditableField, HawbFieldChange, ItemToRemove } from "#/types/manifest";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

/** "Fee: NO → YES", "Packages: 70 → 75". */
const formatValue = (value: HawbFieldChange["from"], t: TFunction): string => {
  if (value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? t("common.yes") : t("common.no");
  return String(value);
};

const FIELD_LABELS: Record<HawbEditableField, string> = {
  arrival_date: "manifests.columns.arrival",
  origin_station: "manifests.columns.origin",
  destination: "manifests.columns.destination",
  responsible_person: "manifests.columns.responsiblePerson",
  packages: "manifests.columns.packages",
  weight_kg: "manifests.columns.weight",
  storage_fee_paid: "manifests.columns.feePaid",
  blocked: "manifests.columns.blocked",
  has_pick_list: "manifests.columns.pickList",
};

const formatFieldChange = (change: HawbFieldChange, t: TFunction): string =>
  `${t(FIELD_LABELS[change.field])}: ${formatValue(change.from, t)} → ${formatValue(change.to, t)}`;

interface HawbChangeSummaryProps {
  changes: HawbFieldChange[];
  itemsToCreate: number;
  itemsToRemove: ItemToRemove[];
}

/**
 * Compact list of what an update / re-import override will do: one line per
 * field change, then the item impact. Tagged items that would be removed are
 * listed by name so the user knows exactly what is lost.
 */
const HawbChangeSummary = ({ changes, itemsToCreate, itemsToRemove }: HawbChangeSummaryProps) => {
  const { t } = useTranslation();
  const tagged = itemsToRemove.filter((item) => item.rfid);

  if (changes.length === 0 && itemsToCreate === 0 && itemsToRemove.length === 0) {
    return <span className="text-sm text-gray-500">{t("manifests.changes.none")}</span>;
  }

  return (
    <ul className="flex flex-col gap-1 text-sm list-disc pl-5">
      {changes.map((change) => (
        <li key={change.field}>{formatFieldChange(change, t)}</li>
      ))}
      {itemsToCreate > 0 && (
        <li className="text-green-700">{t("manifests.changes.itemsCreated", { count: itemsToCreate })}</li>
      )}
      {itemsToRemove.length > 0 && (
        <li className="text-red-700">
          {tagged.length > 0
            ? t("manifests.changes.itemsRemovedTagged", {
                count: itemsToRemove.length,
                tagged: tagged.length,
              })
            : t("manifests.changes.itemsRemoved", { count: itemsToRemove.length })}
          {tagged.length > 0 && (
            <ul className="list-[circle] pl-5 mt-1 text-red-800">
              {tagged.map((item) => (
                <li key={item.id}>
                  {item.external_id ?? `#${item.id}`} · {item.rfid}
                  {item.scanned ? ` · ${t("manifests.changes.scanned")}` : ""}
                </li>
              ))}
            </ul>
          )}
        </li>
      )}
    </ul>
  );
};

export default HawbChangeSummary;
